import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Box as FormActions, Box as FormFields } from '@node-space/ui-components'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { TRANSFER_DESTINATION_RESET_FIELDS } from 'pages/Contacts/constants'
import { useFiatInstrumentForm } from 'pages/Contacts/hooks/useFiatInstrumentForm'
import {
  ContactFiatInstrumentFormData,
  ContactFiatInstrumentFormKeys,
} from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { Steps } from 'pages/Contacts/types/fiatInstruments'
import {
  getFieldsToValidate,
  populateInstrumentFormWithBeneficiaryDetails,
} from 'pages/Contacts/utils/fiatInstruments'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import { BeneficiaryDetailsCurrency, BeneficiaryTransferDestination } from 'types/beneficiaries'
import { ContactCard } from '../ContactCard'
import { ContactsSheets } from '../ContactsSheets'
import { BankAccountDetails } from './BankAccountDetails'
import { FiatInstrumentConfirmation } from './FiatInstrumentConfirmation'
import { IntermediaryBankDetails } from './IntermediaryBankDetails'
import { TransferDestinationSelector } from './TransferDestinationSelector'

export const ContactFiatInstrumentForm = () => {
  const { t } = useTranslation()

  const contact = useAtomValue(contactAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)
  const setContact = useSetAtom(contactAtom)
  const [contactAction, setContactAction] = useAtom(contactActionAtom)

  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)

  const { meta } = contactAction || {}
  const { contactBeneficiary } = meta || {}

  // Used to also get the latest contact value in the useCallback functions
  const contactRef = useRef(contact)
  contactRef.current = contact

  const {
    errors,
    formFieldLabels,
    formValues,
    form,
    isBusinessBankAccount,
    isInternationalBankAccount,
    step,
    clearErrors,
    register,
    setStep,
    setValue,
    trigger,
    setError,
  } = useFiatInstrumentForm()

  const openContactUpdateSheetIfRequired = useCallback(
    (transferDestination: BeneficiaryTransferDestination) => {
      if (!contactRef.current.entity?.address?.region) {
        setContactAction({
          ...contactAction,
          actionId: ContactActionIds.CONTACT,
          header: t('contacts.updateContact'),
          onClose: () => {
            setIsContactSheetOpen(false)
          },
          meta: {
            updateRequiredForBankAccountDestination: transferDestination,
            keepUpdatedContact: true,
          },
        })
        setIsContactSheetOpen(true)
      }
    },
    [contactAction, t]
  )

  useEffect(() => {
    if (contactBeneficiary) {
      handleTransferDestinationSelect(contactBeneficiary?.transferDestination)
      populateInstrumentFormWithBeneficiaryDetails(contactBeneficiary, setValue)
    }
  }, [contactBeneficiary, setValue])

  const handleTransferDestinationSelect = useCallback(
    (transferDestination: BeneficiaryTransferDestination) => {
      const isTransferDestinationChanged =
        transferDestination !== formValues?.transferDestination && !contactBeneficiary
      if (isTransferDestinationChanged) {
        TRANSFER_DESTINATION_RESET_FIELDS.forEach(field => {
          setValue(field as keyof ContactFiatInstrumentFormData, '')
          clearErrors(field as keyof ContactFiatInstrumentFormData)
        })
      }

      setValue('transferDestination', transferDestination)
      const isInternational = transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
      if (isInternational) {
        if (!contactRef.current.entity?.address?.region) {
          openContactUpdateSheetIfRequired(transferDestination)
          return
        }

        setStep(Steps.INTERNATIONAL_BANK_DETAILS)
      } else {
        setStep(Steps.LOCAL_BANK_DETAILS)
      }
    },
    [formValues?.transferDestination]
  )

  const fieldsToValidate: ContactFiatInstrumentFormKeys[] = useMemo(() => {
    return getFieldsToValidate(step)
  }, [step])

  const onContinue = useCallback(async () => {
    const currentFormValues = form?.getValues()

    const currentContact = contactRef.current
    const contactHasRegion = !!currentContact?.entity?.address?.region
    const isInternational =
      currentFormValues.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    const isLocalUsd =
      currentFormValues.transferDestination === BeneficiaryTransferDestination.LOCAL &&
      currentFormValues.currency === BeneficiaryDetailsCurrency.USD

    if ((isInternational || isLocalUsd) && !contactHasRegion) {
      openContactUpdateSheetIfRequired(
        currentFormValues.transferDestination as BeneficiaryTransferDestination
      )
      return
    }

    const isValid = await trigger(fieldsToValidate)

    if (!isValid) {
      return
    }

    switch (step) {
      case Steps.LOCAL_BANK_DETAILS:
        setStep(Steps.CONFIRMATION)
        break
      case Steps.INTERNATIONAL_BANK_DETAILS:
        setStep(Steps.INTERMEDIARY_BANK_DETAILS)
        break
      case Steps.INTERMEDIARY_BANK_DETAILS:
        setStep(Steps.CONFIRMATION)
        break
    }
  }, [
    contactAction,
    contactInstrumentAction,
    fieldsToValidate,
    form,
    setContact,
    setContactAction,
    setStep,
    step,
    t,
    trigger,
    openContactUpdateSheetIfRequired,
  ])

  const actions = useMemo(() => {
    switch (step) {
      case Steps.LOCAL_BANK_DETAILS:
        return (
          <FormActions flex justify="between" paddingT={20}>
            <Button
              testId="cancel"
              type="button"
              variant="secondary"
              onClick={() => {
                contactInstrumentAction?.onClose()
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              testId="add-fiat-instrument"
              type="button"
              variant="primary"
              onClick={onContinue}
            >
              {t('contacts.addBankAccount')}
            </Button>
          </FormActions>
        )
      case Steps.INTERNATIONAL_BANK_DETAILS:
        return (
          <FormActions flex justify="between" paddingT={20}>
            <Button
              testId="cancel"
              type="button"
              variant="secondary"
              onClick={() => {
                contactInstrumentAction?.onClose()
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              type="button"
              testId="add-int-fiat-instrument"
              variant="primary"
              onClick={onContinue}
            >
              {t('continue')}
            </Button>
          </FormActions>
        )
      case Steps.INTERMEDIARY_BANK_DETAILS:
        return (
          <FormActions flex justify="between" paddingT={20}>
            <Button testId="skip-this-step" type="button" variant="secondary" onClick={onContinue}>
              {t('skipThisStep')}
            </Button>
            <Button
              type="button"
              testId="add-int-fiat-instrument"
              variant="primary"
              onClick={onContinue}
            >
              {t('contacts.addBankAccount')}
            </Button>
          </FormActions>
        )
      case Steps.CONFIRMATION:
      case Steps.SUCCESS:
      default:
        return
    }
  }, [step])

  const handleGoBack = () => {
    if (!isInternationalBankAccount) {
      if (step === Steps.LOCAL_BANK_DETAILS) {
        setStep(Steps.TRANSFER_TYPE)
      } else if (step === Steps.CONFIRMATION) {
        setStep(Steps.LOCAL_BANK_DETAILS)
      }
    }

    if (isInternationalBankAccount) {
      if (step === Steps.INTERNATIONAL_BANK_DETAILS) {
        setStep(Steps.TRANSFER_TYPE)
      } else if (step === Steps.INTERMEDIARY_BANK_DETAILS) {
        setStep(Steps.INTERNATIONAL_BANK_DETAILS)
      } else if (step === Steps.CONFIRMATION) {
        setStep(Steps.INTERMEDIARY_BANK_DETAILS)
      }
    }
  }

  const canGoBack = useMemo(() => {
    return (
      step === Steps.LOCAL_BANK_DETAILS ||
      step === Steps.CONFIRMATION ||
      step === Steps.INTERNATIONAL_BANK_DETAILS ||
      step === Steps.INTERMEDIARY_BANK_DETAILS
    )
  }, [step])

  useEffect(() => {
    setContactInstrumentAction({
      ...contactInstrumentAction,
      ...(step === Steps.TRANSFER_TYPE && { header: t('contacts.addBankAccount') }),
      ...(step === Steps.LOCAL_BANK_DETAILS && {
        header: t('contacts.addBankAccount'),
      }),
      ...(step === Steps.INTERNATIONAL_BANK_DETAILS && {
        header: t('contacts.addBankAccount'),
      }),
      ...(step === Steps.INTERMEDIARY_BANK_DETAILS && {
        header: t('intermediaryBankDetails'),
      }),
      ...(step === Steps.CONFIRMATION && { header: t('contacts.confirmBankAccountDetails') }),
      onBack: canGoBack ? handleGoBack : null,
    })
  }, [step, canGoBack])

  return (
    <Box width="full">
      <form data-testid="contact-bank-account-form">
        <FormFields width="full" flexCol gapY={16} paddingT={24}>
          {step === Steps.TRANSFER_TYPE && (
            <TransferDestinationSelector
              onTransferDestinationClick={handleTransferDestinationSelect}
            />
          )}

          {step === Steps.LOCAL_BANK_DETAILS && (
            <Box paddingB={12}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <BankAccountDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                isBusinessAccount={isBusinessBankAccount}
                isInternationalBankAccount={isInternationalBankAccount}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register}
                trigger={trigger}
                setError={setError}
                errors={errors}
                setIsContactSheetOpen={setIsContactSheetOpen}
                contactBeneficiary={contactBeneficiary}
              />
            </Box>
          )}

          {step === Steps.INTERNATIONAL_BANK_DETAILS && (
            <Box paddingB={8}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <BankAccountDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                isBusinessAccount={isBusinessBankAccount}
                isInternationalBankAccount={isInternationalBankAccount}
                setValue={setValue}
                clearErrors={clearErrors}
                trigger={trigger}
                setError={setError}
                register={register}
                errors={errors}
                setIsContactSheetOpen={setIsContactSheetOpen}
                contactBeneficiary={contactBeneficiary}
              />
            </Box>
          )}

          {step === Steps.INTERMEDIARY_BANK_DETAILS && (
            <Box>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <IntermediaryBankDetails
                formValues={formValues}
                errors={errors}
                clearErrors={clearErrors}
                register={register}
                setError={setError}
                setValue={setValue}
                trigger={trigger}
                contactBeneficiary={contactBeneficiary}
              />
            </Box>
          )}

          {step === Steps.CONFIRMATION && (
            <Box paddingB={12}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <FiatInstrumentConfirmation
                formValues={formValues}
                formFieldLabels={formFieldLabels}
                form={form}
                isLoading={false}
              />
            </Box>
          )}
        </FormFields>

        {actions}
      </form>
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
    </Box>
  )
}
